import React, { Suspense, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { useAuthenticator, Authenticator } from '@aws-amplify/ui-react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { useTheme } from './hooks/useTheme';
import { createAppTheme } from './theme';
import LoadingPage from './components/LoadingPage';
import ConfiguringLoader from './components/ConfiguringLoader';
import SessionRecovery from './components/SessionRecovery';

// Public pages
import LoginPage from './pages/LoginPage';

// Lazily-loaded admin/protected pages (issue #2446 — route-level code
// splitting). Each becomes its own content-hashed chunk so the admin-only
// surface is no longer shipped in the entry bundle that signers download.
// Every target must have a `export default` for React.lazy's dynamic import.
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const Policies = React.lazy(() => import('./pages/Policies'));
const PolicyDetail = React.lazy(() => import('./pages/PolicyDetail'));
const Acknowledgments = React.lazy(() => import('./pages/Acknowledgments'));
const Users = React.lazy(() => import('./pages/Users'));
const Groups = React.lazy(() => import('./pages/Groups'));
const ActivityLog = React.lazy(() => import('./pages/ActivityLog'));
const Support = React.lazy(() => import('./pages/Support'));
const SupportDetail = React.lazy(() => import('./pages/SupportDetail'));
const Calendar = React.lazy(() => import('./pages/Calendar'));
const WorkspaceSettings = React.lazy(() => import('./pages/WorkspaceSettings'));
const Templates = React.lazy(() => import('./pages/Templates'));
const BillingPanel = React.lazy(() => import('./components/settings/BillingPanel'));
const DigestFrequencyPanel = React.lazy(() => import('./components/settings/DigestFrequencyPanel'));
const SsoPanel = React.lazy(() => import('./components/settings/SsoPanel'));
const CompliancePanel = React.lazy(() => import('./components/settings/CompliancePanel'));
// #2496 Part 2 — the public auditor viewer page. Lazily loaded so the
// content-hashed chunk is fetched only when an external auditor opens an
// `/auditor/<linkId>` URL; admins paying down the main entry bundle never
// download it. Sits OUTSIDE the auth wrapper (no `<ProtectedRoute>`) — the
// linkId itself is the bearer secret.
const AuditorViewer = React.lazy(() => import('./pages/AuditorViewer'));

// Kept static (in the entry bundle): the unauthenticated/signer surface and
// the small synchronous panels they reach. SignByAuth/SignerPortal/
// AccountSettings + the personal panels are needed on the signer happy path,
// so deferring them would only add a network round-trip without trimming the
// signer's first-load bundle.
import SignByAuth from './pages/SignByAuth';
import SignerPortal from './pages/SignerPortal';
import AccountSettings from './pages/AccountSettings';
import SettingsIndexRedirect from './components/SettingsIndexRedirect';
import AppearancePanel from './components/settings/AppearancePanel';
import ProfilePanel from './components/settings/ProfilePanel';
import NotificationsPanel from './components/settings/NotificationsPanel';
import SecurityPanel from './components/settings/SecurityPanel';
import BillingSuccessPage from './pages/BillingSuccessPage';

// Issue #1477 — the legacy `/sign/:token` magic-link page is removed. In-flight
// URLs from pre-cutover emails are redirected to `/login/:token` (rendering
// `LoginByToken`) so the user still lands on the unified passwordless flow.
// The token hash will not resolve to a `LOGIN_TOKEN` row, so `LoginByToken`'s
// existing "invalid or expired" error path catches it gracefully.

// Public passwordless login pages
//
// NOTE: there is no React route for `/request-login-link`. That legacy URL is
// 301-redirected to `/login` at the CloudFront edge by the
// `infrastructure/src/rewrite-to-index/rewrite-to-index.js` Lambda@Edge
// (issue #1692). Handling it server-side bypasses every client-side React
// Router pitfall that previously regressed three times (#933, #1480, #1692).
import LoginByToken from './pages/LoginByToken';

// Error pages
import NotFoundPage from './pages/NotFoundPage';
import NotFoundInApp from './pages/NotFoundInApp';
import InvalidSigningLinkPage from './pages/InvalidSigningLinkPage';

// Components
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import AuthenticatedLayout from './components/AuthenticatedLayout';
import ErrorBoundary, { RouteErrorBoundary } from './components/ErrorBoundary';
import HelpRedirect from './components/HelpRedirect';

/**
 * Compatibility redirect from the legacy `/sign/:token` URL to the unified
 * `/login/:token` magic-link route (issue #1477). In-flight pre-cutover
 * assignment emails carry the old URL; we keep the redirect for one release
 * cycle. Query parameters and the URL fragment are preserved so any `?next=`
 * style addition rides along. The legacy SIGNING_TOKEN hash will not resolve
 * to a LOGIN_TOKEN row, so `LoginByToken`'s "invalid or expired" branch
 * surfaces the right user-facing message.
 */
function RedirectSignToLogin() {
    const { token } = useParams<{ token: string }>();
    const location = useLocation();
    const safeToken = token ?? '';
    return <Navigate to={`/login/${safeToken}${location.search}${location.hash}`} replace />;
}

function App() {
    const { isDark } = useTheme();
    const theme = useMemo(() => createAppTheme(isDark), [isDark]);

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <ErrorBoundary>
                <Authenticator.Provider>
                    <AppRoutes />
                </Authenticator.Provider>
            </ErrorBoundary>
        </ThemeProvider>
    );
}

function AppRoutes() {
    const { authStatus } = useAuthenticator((context) => [context.authStatus]);

    // Always mount <Router>; render the loading spinner INSIDE it. Returning
    // different element types at the same React tree position (a <div> while
    // configuring vs <Router> after) forces React to unmount/remount the
    // Router on every authStatus transition. The remounted Router reads
    // window.location.pathname afresh, dropping any pending URL updates from
    // any useNavigate calls on the previous Router instance.
    return (
        <Router>
            {/*
             * Always-mounted session-recovery wiring (issue #2595). Lives INSIDE
             * the Router (it calls useNavigate) but renders no UI; it handles the
             * bounded session re-check on app mount and on idle-return
             * visibilitychange, regardless of authStatus. It must NOT live in
             * useCurrentUser / AuthenticatedLayout — those mount only after
             * authStatus === 'authenticated', so they never run during the stuck
             * 'configuring' state we're recovering from.
             */}
            <SessionRecovery />
            {authStatus === 'configuring' ? (
                <ConfiguringLoader />
            ) : (
                // Single Suspense boundary for every lazily-loaded route
                // (issue #2446). While an admin chunk is in flight the
                // full-page LoadingPage spinner renders; the per-route
                // RouteErrorBoundary still wraps each element to catch a
                // failed chunk fetch.
                <Suspense fallback={<LoadingPage />}>
                    <Routes>
                        {/* Public routes */}
                        <Route path="/" element={<Navigate to="/login" replace />} />
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/sign" element={<InvalidSigningLinkPage />} />
                        <Route path="/sign/:token" element={<RedirectSignToLogin />} />
                        {/*
                         * `/request-login-link` is intentionally NOT a React route. It is
                         * 301-redirected to `/login` at the CloudFront edge by the
                         * rewrite-to-index Lambda@Edge (issue #1692). If the request
                         * somehow falls through (e.g. running the SPA against the dev
                         * server with no edge function in front of it), the catch-all
                         * `<NotFoundPage />` will render — which is fine for a legacy
                         * route that should never be hit in a deployed environment.
                         */}
                        <Route path="/login/:token" element={<LoginByToken />} />
                        {/*
                         * #2496 Part 2 — public auditor share-link viewer.
                         * MUST stay outside `ProtectedRoute`/`AdminRoute`: an
                         * external auditor opens the URL in a fresh browser
                         * with no Accedo account. The `:linkId` is the
                         * bearer secret; the viewer fetches the packet via
                         * the unauthenticated `GET /api/policy/auditor/:linkId`
                         * route using `X-Aws-Account` header from
                         * `getSigningAwsAccount()` (no JWT). Authenticated
                         * admins previewing their own link render the same
                         * bare branded shell — no sidebar / no avatar.
                         */}
                        <Route
                            path="/auditor/:linkId"
                            element={
                                <RouteErrorBoundary>
                                    <AuditorViewer />
                                </RouteErrorBoundary>
                            }
                        />

                        {/* Protected routes */}
                        <Route
                            path="/dashboard"
                            element={
                                <ProtectedRoute>
                                    <AuthenticatedLayout>
                                        <RouteErrorBoundary>
                                            <Dashboard />
                                        </RouteErrorBoundary>
                                    </AuthenticatedLayout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/policies"
                            element={
                                <ProtectedRoute>
                                    <AuthenticatedLayout>
                                        <AdminRoute>
                                            <RouteErrorBoundary>
                                                <Policies />
                                            </RouteErrorBoundary>
                                        </AdminRoute>
                                    </AuthenticatedLayout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/policies/:id"
                            element={
                                <ProtectedRoute>
                                    <AuthenticatedLayout>
                                        <AdminRoute>
                                            <RouteErrorBoundary>
                                                <PolicyDetail />
                                            </RouteErrorBoundary>
                                        </AdminRoute>
                                    </AuthenticatedLayout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/acknowledgments"
                            element={
                                <ProtectedRoute>
                                    <AuthenticatedLayout>
                                        <AdminRoute>
                                            <RouteErrorBoundary>
                                                <Acknowledgments />
                                            </RouteErrorBoundary>
                                        </AdminRoute>
                                    </AuthenticatedLayout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/users"
                            element={
                                <ProtectedRoute>
                                    <AuthenticatedLayout>
                                        <AdminRoute>
                                            <RouteErrorBoundary>
                                                <Users />
                                            </RouteErrorBoundary>
                                        </AdminRoute>
                                    </AuthenticatedLayout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/groups"
                            element={
                                <ProtectedRoute>
                                    <AuthenticatedLayout>
                                        <AdminRoute>
                                            <RouteErrorBoundary>
                                                <Groups />
                                            </RouteErrorBoundary>
                                        </AdminRoute>
                                    </AuthenticatedLayout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/activity-log"
                            element={
                                <ProtectedRoute>
                                    <AuthenticatedLayout>
                                        <AdminRoute>
                                            <RouteErrorBoundary>
                                                <ActivityLog />
                                            </RouteErrorBoundary>
                                        </AdminRoute>
                                    </AuthenticatedLayout>
                                </ProtectedRoute>
                            }
                        />
                        {/* #2433 — Support routes follow the plural-resource
                        convention used across the app (`/policies`,
                        `/users`, `/groups`, etc.). Canonical URLs are
                        `/support/tickets` (list) and `/support/tickets/:id`
                        (detail). `/support` redirects to the list so the
                        sidebar link still works. Previously
                        `/support/:ticketId` caught the literal "tickets"
                        path-segment as an id and rendered a raw backend
                        404 leaking the tenant id. */}
                        <Route path="/support" element={<Navigate to="/support/tickets" replace />} />
                        <Route
                            path="/support/tickets"
                            element={
                                <ProtectedRoute>
                                    <AuthenticatedLayout>
                                        <AdminRoute>
                                            <RouteErrorBoundary>
                                                <Support />
                                            </RouteErrorBoundary>
                                        </AdminRoute>
                                    </AuthenticatedLayout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/support/tickets/:ticketId"
                            element={
                                <ProtectedRoute>
                                    <AuthenticatedLayout>
                                        <AdminRoute>
                                            <RouteErrorBoundary>
                                                <SupportDetail />
                                            </RouteErrorBoundary>
                                        </AdminRoute>
                                    </AuthenticatedLayout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/calendar"
                            element={
                                <ProtectedRoute>
                                    <AuthenticatedLayout>
                                        <AdminRoute>
                                            <RouteErrorBoundary>
                                                <Calendar />
                                            </RouteErrorBoundary>
                                        </AdminRoute>
                                    </AuthenticatedLayout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/policies/:id/sign"
                            element={
                                <ProtectedRoute>
                                    <AuthenticatedLayout>
                                        <RouteErrorBoundary>
                                            <SignByAuth />
                                        </RouteErrorBoundary>
                                    </AuthenticatedLayout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/signer"
                            element={
                                <ProtectedRoute>
                                    <AuthenticatedLayout>
                                        <RouteErrorBoundary>
                                            <SignerPortal />
                                        </RouteErrorBoundary>
                                    </AuthenticatedLayout>
                                </ProtectedRoute>
                            }
                        />
                        {/*
                         * Personal Settings (`/account/*`, issue #2114) — every
                         * authenticated user, no admin gate. Hosts the four
                         * personal panels: Appearance, Profile, Notifications,
                         * Security. Reached via the user-avatar menu's
                         * "Personal Settings" item.
                         *
                         * Bare `/account` index-redirects to `/account/profile`.
                         */}
                        <Route
                            path="/account"
                            element={
                                <ProtectedRoute>
                                    <AuthenticatedLayout>
                                        <RouteErrorBoundary>
                                            <AccountSettings />
                                        </RouteErrorBoundary>
                                    </AuthenticatedLayout>
                                </ProtectedRoute>
                            }
                        >
                            <Route index element={<Navigate to="profile" replace />} />
                            <Route path="appearance" element={<AppearancePanel />} />
                            <Route path="profile" element={<ProfilePanel />} />
                            <Route path="notifications" element={<NotificationsPanel />} />
                            <Route path="security" element={<SecurityPanel />} />
                        </Route>
                        {/*
                         * Workspace Settings (`/settings/*`, issue #2114) —
                         * admins only. Hosts the three workspace panels:
                         * Billing, Digest, Single Sign-On. Reached via the
                         * main app nav's "Settings" entry.
                         *
                         * Access control:
                         * - The whole subtree is wrapped in `<AdminRoute>` with
                         *   `redirectTo="/account/profile"` so signers
                         *   deep-linking into `/settings/billing` etc. land on
                         *   their own personal settings rather than being
                         *   bounced all the way back to `/signer`.
                         * - Bare `/settings` is handled by
                         *   `<SettingsIndexRedirect />` which sends admins to
                         *   `/settings/billing` (preserves the historical
                         *   default since #1877) and signers to
                         *   `/account/profile`.
                         *
                         * Legacy redirects (PERMANENT): the `/settings/account/*`
                         * and `/settings/workspace/*` URLs from #2076 still appear
                         * in already-sent emails, bookmarks, and help articles;
                         * they 30x-redirect to the new locations below.
                         */}
                        <Route
                            path="/settings"
                            element={
                                <ProtectedRoute>
                                    <AuthenticatedLayout>
                                        <RouteErrorBoundary>
                                            <AdminRoute redirectTo="/account/profile">
                                                <WorkspaceSettings />
                                            </AdminRoute>
                                        </RouteErrorBoundary>
                                    </AuthenticatedLayout>
                                </ProtectedRoute>
                            }
                        >
                            <Route index element={<SettingsIndexRedirect />} />
                            <Route path="billing" element={<BillingPanel />} />
                            <Route path="digest" element={<DigestFrequencyPanel />} />
                            <Route path="sso" element={<SsoPanel />} />
                            <Route path="compliance" element={<CompliancePanel />} />
                        </Route>
                        {/*
                         * Legacy /settings/account/* → /account/* redirects
                         * (issue #2114). These are PERMANENT — emails sent
                         * during the #2076 window and merged help articles
                         * carry these URLs.
                         */}
                        <Route path="/settings/account" element={<Navigate to="/account/profile" replace />} />
                        <Route path="/settings/account/appearance" element={<Navigate to="/account/appearance" replace />} />
                        <Route path="/settings/account/profile" element={<Navigate to="/account/profile" replace />} />
                        <Route path="/settings/account/notifications" element={<Navigate to="/account/notifications" replace />} />
                        <Route path="/settings/account/security" element={<Navigate to="/account/security" replace />} />
                        {/*
                         * Legacy /settings/workspace/* → /settings/* redirects
                         * (issue #2114). Same rationale as above — emails sent
                         * during the #2076 window and the post-#2076 help
                         * articles use these URLs.
                         */}
                        <Route path="/settings/workspace" element={<Navigate to="/settings/billing" replace />} />
                        <Route path="/settings/workspace/billing" element={<Navigate to="/settings/billing" replace />} />
                        <Route path="/settings/workspace/digest" element={<Navigate to="/settings/digest" replace />} />
                        <Route path="/settings/workspace/sso" element={<Navigate to="/settings/sso" replace />} />
                        <Route
                            path="/templates"
                            element={
                                <ProtectedRoute>
                                    <AuthenticatedLayout>
                                        <AdminRoute>
                                            <RouteErrorBoundary>
                                                <Templates />
                                            </RouteErrorBoundary>
                                        </AdminRoute>
                                    </AuthenticatedLayout>
                                </ProtectedRoute>
                            }
                        />
                        {/*
                         * Stripe checkout success landing (issue #1877). NOT wrapped
                         * in AdminRoute — Stripe may redirect here for any
                         * authenticated user who just paid; the heavy lifting
                         * happens server-side via the checkout.session.completed
                         * webhook, so the page is purely a visual confirmation.
                         */}
                        <Route
                            path="/billing/success"
                            element={
                                <ProtectedRoute>
                                    <AuthenticatedLayout>
                                        <RouteErrorBoundary>
                                            <BillingSuccessPage />
                                        </RouteErrorBoundary>
                                    </AuthenticatedLayout>
                                </ProtectedRoute>
                            }
                        />

                        {/*
                         * /help/* — redirects to the branding-site help center
                         * (#2420). The /help articles live on the branding site,
                         * not in the SPA, so an admin typing `/help` (or any
                         * `/help/<slug>`) into the URL bar from a tenant subdomain
                         * used to fall through to the catch-all below and render
                         * the in-app 404. This dedicated route fires a
                         * `window.location.replace(helpUrl())` so the user lands on
                         * the help center instead. Mounted ABOVE the catch-all so
                         * unknown paths still 404 correctly; served regardless of
                         * `authStatus` so a logged-out admin typing `/help` also
                         * reaches the public help center.
                         */}
                        <Route path="/help/*" element={<HelpRedirect />} />

                        {/*
                         * Catch-all route. Authenticated users get the in-app 404
                         * (sidebar + avatar preserved via AuthenticatedLayout, CTA
                         * routes back to /dashboard); unauthenticated visitors get
                         * the public marketing 404 with its "Sign In" CTA. See
                         * issue #1765 — without the auth-aware split, an admin
                         * who typo'd a URL saw the marketing shell and a misleading
                         * "Sign In" button.
                         */}
                        {authStatus === 'authenticated' ? (
                            <Route
                                path="*"
                                element={
                                    <ProtectedRoute>
                                        <AuthenticatedLayout>
                                            <RouteErrorBoundary>
                                                <NotFoundInApp />
                                            </RouteErrorBoundary>
                                        </AuthenticatedLayout>
                                    </ProtectedRoute>
                                }
                            />
                        ) : (
                            <Route path="*" element={<NotFoundPage />} />
                        )}
                    </Routes>
                </Suspense>
            )}
        </Router>
    );
}

export default App;
